import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Clock, Star, Check, Send, Phone, MessageCircle, Calendar, Users, Building2, Plane, Info } from 'lucide-react';
import { internationalTours, indiaTours } from '../mock';
import SafeImage from '../components/SafeImage';
import OfferRibbon from '../components/OfferRibbon';
import PriceTag from '../components/PriceTag';
import WhatsAppShareBtn from '../components/WhatsAppShareBtn';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { submitEnquiry, API } from '../api';
import useAntiSpam from '../hooks/useAntiSpam';
import { useSettings } from '../store';
import { applyPageSEO } from '../utils/pageSEO';

const mockPackages = [...internationalTours, ...indiaTours];

const PackageDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();

  // Try mock first (has `id`); backend uses `pkg_code`
  const mockMatch = useMemo(() => mockPackages.find((p) => p.id === id), [id]);
  const [pkg, setPkg] = useState(mockMatch || null);

  useEffect(() => {
    // Fetch backend package by pkg_code (which mirrors mock `id`)
    axios.get(`${API}/packages/${id}`)
      .then((res) => {
        if (res.data) {
          setPkg({
            ...res.data,
            id: res.data.pkg_code,
          });
        }
      })
      .catch(() => {});
  }, [id]);

  // SEO meta tags
  useEffect(() => {
    if (!pkg) return;
    const desc = pkg.seo_description || `${pkg.title} \u2014 ${pkg.duration || ''}. From INR ${Math.round((pkg.price || 0)).toLocaleString('en-IN')}. Book with Travelsera.`;
    return applyPageSEO({
      title: pkg.seo_title || pkg.title,
      description: desc,
      noIndex: !!pkg.seo_no_index,
      noFollow: !!pkg.seo_no_follow,
    });
  }, [pkg]);

  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const { fields: antiSpamFields, honeypotInput, readHoneypot } = useAntiSpam();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  if (!pkg) {
    return (
      <div className="min-h-screen">
        <div className="bg-[#0d64a8] h-24" />
        <div className="max-w-lg mx-auto py-24 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Package not found</h2>
          <p className="text-gray-600 mb-6">The tour you’re looking for may have been moved.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#0d64a8] text-white px-5 py-2.5 rounded-md">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  const inclusionsDefault = [
    'Return airport transfers',
    'Accommodation as per itinerary',
    'Daily breakfast at hotel',
    'Sightseeing with English-speaking guide',
    'Entry tickets to listed attractions',
    'All applicable taxes',
  ];
  const exclusionsDefault = [
    'International / domestic flights (unless mentioned)',
    'Visa fees & travel insurance',
    'Lunch and dinner (unless specified)',
    'Personal expenses (laundry, calls, minibar)',
    'Anything not listed in inclusions',
  ];
  const highlightsDefault = [
    'Handpicked 3–5 star hotels',
    'Private AC vehicle throughout',
    'English speaking guide',
    'Daily breakfast included',
    'All entry tickets covered',
    '24/7 on-trip support',
  ];

  const inclusions = (Array.isArray(pkg.inclusions) && pkg.inclusions.length > 0) ? pkg.inclusions : inclusionsDefault;
  const exclusions = (Array.isArray(pkg.exclusions) && pkg.exclusions.length > 0) ? pkg.exclusions : exclusionsDefault;
  const highlights = (Array.isArray(pkg.highlights) && pkg.highlights.length > 0) ? pkg.highlights : highlightsDefault;
  const overviewText = (pkg.overview || '').trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      setErr('Please fill name and phone');
      return;
    }
    setErr('');
    setLoading(true);
    try {
      await submitEnquiry({
        ...form,
        package_id: pkg.id,
        package_title: pkg.title,
        source: 'detail_page',
        ...antiSpamFields,
        website: readHoneypot(),
      });
      setSent(true);
      setForm({ name: '', phone: '', email: '', message: '' });
      setTimeout(() => setSent(false), 4000);
    } catch (e2) {
      setErr('Could not send enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header on solid blue backdrop */}
      <div className="relative bg-[#0d64a8]">
        <Header />
        <div className="h-24" />
      </div>

      {/* Hero */}
      <section className="relative h-[380px] overflow-hidden">
        <SafeImage src={pkg.image} alt={pkg.title} fallbackType={pkg.fbType || 'default'} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        {pkg.offer_enabled && (
          <OfferRibbon text={pkg.offer_ribbon_text || 'Limited Offer'} endsAt={pkg.offer_ends_at} variant="detail" />
        )}
        <div className="absolute inset-0 max-w-[1280px] mx-auto px-5 lg:px-8 flex flex-col justify-end pb-8">
          <button onClick={() => nav(-1)} className="absolute top-6 left-5 lg:left-8 flex items-center gap-1.5 text-white/90 hover:text-white text-sm">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2 text-white/90 text-xs mb-2">
            <span className="bg-[#ffcc29] text-[#0a2a4a] px-2 py-0.5 rounded font-semibold">TOUR #{pkg.id}</span>
            <span className="flex items-center gap-1"><Clock size={12}/> {pkg.duration}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow max-w-3xl">{pkg.title}</h1>
          <div className="flex items-center gap-2 text-white/90 mt-2">
            <MapPin size={16} /> <span className="text-sm">{pkg.itinerary}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-10">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left */}
          <div className="lg:col-span-2 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: Clock, label: 'Duration', value: pkg.duration },
                { icon: MapPin, label: 'Destinations', value: pkg.itinerary.split(',').length + ' cities' },
                { icon: Users, label: 'Group Size', value: '2 - 30 pax' },
              ].map((s, i) => {
                const Ic = s.icon;
                return (
                  <div key={i} className="bg-[#f7fafc] rounded-lg p-4 flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-[#0d64a8]/10 text-[#0d64a8] flex items-center justify-center shrink-0"><Ic size={18}/></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-gray-500 uppercase tracking-wide">{s.label}</div>
                      <div className="text-sm font-semibold text-gray-800 truncate">{s.value}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Overview</h2>
              {overviewText ? (
                <p className="text-gray-700 leading-relaxed text-[15px] whitespace-pre-line" data-testid="pkg-overview">
                  {overviewText}
                </p>
              ) : (
                <p className="text-gray-700 leading-relaxed text-[15px]" data-testid="pkg-overview">
                  Discover the wonders of <strong>{pkg.title}</strong> with our carefully curated {(pkg.duration || '').toLowerCase()} journey. Travel across {pkg.itinerary}—every stay, transfer and sightseeing is handled by our on-ground experts so you can just relax and soak in the experience. Perfect for couples, families and small groups looking for a stress-free premium getaway.
                </p>
              )}
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Highlights</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pkg-highlights">
                {highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="text-green-600 mt-0.5 shrink-0" size={16} />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Inclusions</h3>
                <ul className="space-y-2" data-testid="pkg-inclusions">
                  {inclusions.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="text-green-600 mt-0.5 shrink-0" size={15} /> {it}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Exclusions</h3>
                <ul className="space-y-2" data-testid="pkg-exclusions">
                  {exclusions.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-red-500 mt-0.5 shrink-0">✕</span> {it}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Share this deal — sits directly below the two-column inclusions/exclusions */}
            <div className="border-t border-gray-100 pt-5 pb-1" data-testid="pkg-share-section">
              <WhatsAppShareBtn
                pkg={{ ...pkg, pkg_code: pkg.pkg_code || pkg.id }}
                variant="circle-text"
              />
              <p className="text-[11px] text-gray-400 mt-1.5">
                Love this trip? Send it to a friend in one tap.
              </p>
            </div>

            {/* Hotels */}
            {pkg.show_hotels !== false && Array.isArray(pkg.hotels) && pkg.hotels.length > 0 && (
              <div data-testid="pkg-hotels-section">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="text-[#0d64a8]" size={18} />
                  <h2 className="text-xl font-bold text-gray-900">Hotels</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pkg.hotels.map((h, i) => {
                    const images = [h.image, h.image2].filter(Boolean);
                    return (
                      <div key={i} data-testid={`pkg-hotel-${i}`} className="border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col">
                        {images.length > 0 && (
                          <div className={`grid ${images.length === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-0.5 bg-gray-100`}>
                            {images.map((src, k) => (
                              <img
                                key={k}
                                src={src}
                                alt={`${h.name} photo ${k + 1}`}
                                className="w-full h-40 object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ))}
                          </div>
                        )}
                        <div className="p-4 flex-1 flex flex-col">
                          <div className="font-semibold text-gray-900 text-sm">{h.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {[h.city, h.category].filter(Boolean).join(' · ')}
                          </div>
                          {h.nights && <div className="text-xs text-[#0d64a8] mt-1 font-medium">{h.nights} night{String(h.nights) === '1' ? '' : 's'}</div>}
                          {h.notes && <div className="text-xs text-gray-600 mt-1">{h.notes}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Flights */}
            {pkg.show_flights !== false && Array.isArray(pkg.flights) && pkg.flights.length > 0 && (
              <div data-testid="pkg-flights-section">
                <div className="flex items-center gap-2 mb-3">
                  <Plane className="text-[#0d64a8]" size={18} />
                  <h2 className="text-xl font-bold text-gray-900">Flights</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-[#0d64a8] text-white">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Airline</th>
                        <th className="text-left px-3 py-2 font-medium">Flight</th>
                        <th className="text-left px-3 py-2 font-medium">Route</th>
                        <th className="text-left px-3 py-2 font-medium">Depart</th>
                        <th className="text-left px-3 py-2 font-medium">Arrive</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pkg.flights.map((f, i) => (
                        <tr key={i} data-testid={`pkg-flight-${i}`} className={i % 2 ? 'bg-white' : 'bg-[#f7fafc]'}>
                          <td className="px-3 py-2 text-gray-800">{f.airline || '-'}</td>
                          <td className="px-3 py-2 text-gray-800">{f.flight_no || '-'}</td>
                          <td className="px-3 py-2 text-gray-800">{[f.from_city, f.to_city].filter(Boolean).join(' → ') || '-'}</td>
                          <td className="px-3 py-2 text-gray-700">{f.depart || '-'}</td>
                          <td className="px-3 py-2 text-gray-700">{f.arrive || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Additional Information */}
            {pkg.additional_info && pkg.additional_info.trim() && (
              <div data-testid="pkg-additional-info-section">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="text-[#0d64a8]" size={18} />
                  <h2 className="text-xl font-bold text-gray-900">Additional Information</h2>
                </div>
                <div className="bg-[#f7fafc] border border-gray-200 rounded-lg p-5">
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid="pkg-additional-info">
                    {pkg.additional_info}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right - Booking card */}
          <div className="lg:sticky lg:top-6 h-fit">
            <div className="bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden">
              <div className="bg-[#0d64a8] text-white px-5 py-4">
                <PriceTag
                  price={pkg.price}
                  offerEnabled={pkg.offer_enabled}
                  offerPrice={pkg.offer_price}
                  size="lg"
                  suffix="/ adult"
                  onDark
                />
                <div className="flex items-center gap-1 mt-1 text-[#ffcc29] text-xs">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={12} className="fill-[#ffcc29]" />)}
                  <span className="text-white/90 ml-1">4.9 (218 reviews)</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Send an Enquiry</h3>
                {sent ? (
                  <div className="py-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
                      <Send size={22} />
                    </div>
                    <p className="font-medium text-gray-800">Enquiry Sent!</p>
                    <p className="text-sm text-gray-500 mt-1">Our travel expert will call you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-2.5">
                    {honeypotInput}
                    <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Your Name*" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#0d64a8]" />
                    <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Phone Number*" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#0d64a8]" />
                    <input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email (optional)" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#0d64a8]" />
                    <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={3} placeholder="Preferred travel dates, group size, questions..." className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#0d64a8] resize-none" />
                    {err && <div className="text-xs text-red-600">{err}</div>}
                    <button disabled={loading} className="w-full bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-70 text-white font-medium py-2.5 rounded-md flex items-center justify-center gap-2">
                      {loading ? 'Sending...' : (<><Send size={14}/> Submit Enquiry</>)}
                    </button>
                  </form>
                )}

                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-2">
                  <a href="tel:+919650336344" className="flex items-center justify-center gap-1.5 border border-gray-200 rounded-md py-2 text-sm text-gray-700 hover:border-[#0d64a8] hover:text-[#0d64a8]">
                    <Phone size={14}/> Call
                  </a>
                  <a href="https://wa.me/919650336344" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 border border-gray-200 rounded-md py-2 text-sm text-gray-700 hover:border-[#25D366] hover:text-[#25D366]">
                    <MessageCircle size={14}/> WhatsApp
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-[#fff8e1] border border-[#ffcc29]/40 text-[#0a2a4a] rounded-lg p-4 mt-4 text-sm flex items-start gap-2">
              <Calendar size={16} className="text-[#0d64a8] mt-0.5"/>
              <div>
                <div className="font-semibold mb-1">Flexible dates?</div>
                <div className="text-gray-700">We can shift departures to match your calendar. Just mention your preferred window in the enquiry.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PackageDetail;
